import { SupportChains } from '@/config';
import { useAppDispatch, useDelayEffect, useWallet, WalletContext } from '@/services';
import { unlink, link } from '@/services/reducers/wallet';
import { useConnectWallet } from '@web3-onboard/react';
import { ReactElement, useEffect, useRef } from 'react';
import { notification } from 'antd';
import i18n from '@/i18n';
import { formatString, sensitiveAddress } from '@/utils';
import { createWalletSessionKey } from '@/services/wallet-session';
import { clearPendingWalletChainSwitch, isPendingWalletChainSwitchTarget, shouldWaitForPendingWalletChainSwitch } from '@/services/wallet-chain-switch';
const WalletStateSettleDelaySeconds = 0.2;

const clearWalletContext = () => {
    WalletContext.Provider = null;
    WalletContext.Wallet = undefined;
    WalletContext.Chain = undefined;
    WalletContext.Address = '';
};

const WalletInit = (): ReactElement => {
    const [{ wallet }, , disconnectWallet] = useConnectWallet();
    const dispatch = useAppDispatch();
    const { address, chain, chainId, disconnect: disc, logged } = useWallet();
    const lastLinkedWalletKey = useRef('');
    const lastLoginSuccessKey = useRef('');
    useEffect(() => {
        if (disc <= 0) return;
        const activeWallet = wallet || WalletContext.Onboard?.state.get().wallets[0];
        if (!activeWallet) {
            lastLinkedWalletKey.current = '';
            clearWalletContext();
            dispatch(unlink());
            return;
        }
        disconnectWallet({ label: activeWallet.label }).finally(() => {
            lastLinkedWalletKey.current = '';
            clearWalletContext();
            dispatch(unlink());
        });
    }, [disc, disconnectWallet, dispatch, wallet]);

    useDelayEffect(
        () => {
            let chain: WalletChain | undefined = undefined;
            if (wallet) {
                var chainId = parseInt(wallet.chains[0].id, 16);
                // 获取 chain
                chain = SupportChains.find((o) => o.chainId == chainId);
                WalletContext.Provider = wallet.provider;
                WalletContext.Wallet = wallet;
                WalletContext.Chain = chain;
            }
            // 如果不存在则忽略
            if (!wallet || !chain) {
                lastLinkedWalletKey.current = '';
                clearWalletContext();
                dispatch(unlink());
                return;
            }
            if (shouldWaitForPendingWalletChainSwitch(chain.chainId)) {
                return;
            }
            if (isPendingWalletChainSwitchTarget(chain.chainId)) {
                clearPendingWalletChainSwitch();
            }
            WalletContext.Address = wallet.accounts[0].address;
            const walletKey = createWalletSessionKey({ address: wallet.accounts[0].address, chainId: chain.chainId });
            if (lastLinkedWalletKey.current === walletKey) return;
            lastLinkedWalletKey.current = walletKey;
            dispatch(
                link({
                    chainId: chain.chainId,
                    chain: chain,
                    address: wallet.accounts[0].address,
                    connected: true,
                    logged: false,
                    type: wallet.label,
                })
            );
        },
        [wallet],
        WalletStateSettleDelaySeconds
    );

    useEffect(() => {
        if (!logged || !address || !chain) return;
        const loginKey = `${address.toLowerCase()}:${chainId}`;
        if (lastLoginSuccessKey.current === loginKey) return;
        lastLoginSuccessKey.current = loginKey;
        const msg = i18n.t('login-success.message', { ns: 'wallet' });
        notification.success({ message: formatString(msg, sensitiveAddress(address), chain.label || chain.name) });
    }, [address, chain, chainId, logged]);

    return <></>;
};

export default WalletInit;
